import React, { useState } from 'react';
import { AlertTriangle, Scale, DollarSign, Target, ShoppingBag, Zap, Send, MessageCircle } from 'lucide-react';
import CounterAnimation from './CounterAnimation';
import RegistrationModal from './RegistrationModal';

const WhyImportantSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };
  const reasons = [
    {
      icon: AlertTriangle,
      title: 'Prevent Brand Confusion',
      description: 'Without trademark protection, competitors can use similar names or logos, confusing customers and diluting your brand identity. A registered trademark gives you exclusive rights to your mark.',
      statNum: 70,
      statSuffix: '%',
      statLabel: 'of businesses face brand confusion'
    },
    {
      icon: Scale,
      title: 'Legal Enforcement Power',
      description: 'Federal registration provides you with legal standing to sue for infringement in federal court and opens doors to statutory damages and attorney fees recovery.',
      statNum: 3,
      statSuffix: 'x',
      statLabel: 'easier to win infringement cases'
    },
    {
      icon: DollarSign,
      title: 'Protect Your Investment',
      description: "You've invested time and money building your brand. A trademark protects that investment and prevents others from capitalizing on your hard-earned reputation.",
      statNum: 100,
      statPrefix: '$',
      statSuffix: 'K+',
      statLabel: 'average rebranding cost'
    }
  ];

  const risks = [
    {
      icon: Target,
      text: 'Someone else registers your brand name first'
    },
    {
      icon: ShoppingBag,
      text: 'Competitors create confusion in the marketplace'
    },
    {
      icon: Zap,
      text: 'You invest in marketing a brand you cannot legally protect'
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Critical Information
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Why Trademark Registration is Important
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            In today's competitive marketplace, trademark registration isn't just recommended—it's essential for any serious business owner
          </p>
        </div>

        {/* Main Reasons */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <reason.icon className="w-8 h-8 text-[#FF6B35]" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#FF6B35]">
                    <CounterAnimation 
                      end={reason.statNum} 
                      prefix={reason.statPrefix || ''} 
                      suffix={reason.statSuffix || ''} 
                    />
                  </div>
                  <div className="text-xs text-gray-500">{reason.statLabel}</div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">
                {reason.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>

        {/* Risk Section */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 md:p-12 border-2 border-red-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Header + Risks */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-black">
                  The Cost of Waiting
                </h3>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                Every day without trademark protection puts your brand at risk. Here's what could happen:
              </p>
              <div className="space-y-4">
                {risks.map((risk, index) => (
                  <div key={index} className="flex items-start gap-3 group cursor-default">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <risk.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 pt-1">{risk.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Real-World Impact */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-black mb-6 text-center">
                Real-World Impact
              </h4>
              <div className="space-y-6">
                <div className="border-l-4 border-[#FF6B35] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "A competitor started using a name similar to ours. Without trademark protection, we had no legal recourse and had to rebrand entirely."
                  </p>
                  <p className="text-sm text-gray-500">- Sarah M., E-commerce Owner</p>
                </div>
                <div className="border-l-4 border-[#FF6B35] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "We spent $150K on marketing before discovering someone else owned the trademark. Starting over was devastating."
                  </p>
                  <p className="text-sm text-gray-500">- Michael T., Tech Startup Founder</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-2xl font-bold text-[#FF6B35] mb-2">Don't Let This Be You</p>
                <p className="text-gray-600">Start your trademark registration today</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons - Below the entire section */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => openModal('registration')}
            className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Start Registration
          </button>
          <button
            onClick={() => {
              if (window.cd360Api && window.cd360Api.liveChat) {
                window.cd360Api.liveChat.showWebWidget();
              } else {
                console.log('Chat widget not loaded yet');
              }
            }}
            className="bg-white border-2 border-[#FF6B35] text-[#FF6B35] px-8 py-4 rounded-lg font-semibold hover:bg-[#FF6B35] hover:text-white transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            Discuss with Attorney
          </button>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default WhyImportantSection;